import { gsap } from "gsap";

const SELECTORS = {
  tab: ".js-tab",
  tabpanel: ".js-tabpanel",
  tabpanelClose: ".js-tabpanel-close",
  tabpanelTabs: ".js-tab-animate",
  tabpanelHeader: ".js-header-animate",
  tabpanelImage: ".js-image-animate",
  tabpanelQuote: ".js-quote-animate",
  tabpanelSearch: ".js-search-animate",
};

const CLASSES = {
  hidden: "hidden",
  active: "tab-active",
};

class Tabs {
  constructor(context, tabSelector = SELECTORS.tab, mask) {
    this.context = context;
    this.tabs = null;
    this.tabSelector = tabSelector;
    this.onToggleCbStack = [];
    this.onCloseCbStack = [];
    this.tabpanelAnimations = [];
    this.mask = mask;
    this.reducedMotionActive = false;
  }

  getAssociatedTab(tabUid) {
    const tab = this.tabs.filter((tab) => tab.dataset.tab === tabUid);

    if (tab.length > 0) {
      return tab[0];
    }

    return null;
  }

  getAssociatedTabpanel(tabUid) {
    const tabpanel = this.tabpanels.filter(
      (tabpanel) => tabpanel.dataset.tab === tabUid
    );

    if (tabpanel.length > 0) {
      return tabpanel[0];
    }

    return null;
  }

  init() {
    this.tabs = [...this.context.querySelectorAll(this.tabSelector)];
    this.tabpanels = [...this.context.querySelectorAll(SELECTORS.tabpanel)];
    this.tabpanelDismissers = [
      ...this.context.querySelectorAll(SELECTORS.tabpanelClose),
    ];

    if (this.mask) {
      this.tabs.forEach((tab) => this.setTabpanelAnimation(tab));
    }

    this.subscribe();
  }

  setTabpanelAnimation(tab) {
    const currentPanel = this.tabpanels.filter(
      (tabpanel) => tabpanel.dataset.tab === tab.dataset.tab
    );
    const tabpanel = currentPanel[0];
    const tabType = tab.dataset.type;

    if (!!currentPanel.length && tabType !== "search") {
      const tabpanelHeader = tabpanel.querySelector(SELECTORS.tabpanelHeader);
      const tabpanelTabs = [
        ...tabpanel.querySelectorAll(SELECTORS.tabpanelTabs),
      ];
      const tabpanelImage = tabpanel.querySelector(SELECTORS.tabpanelImage);
      const tabpanelQuote = tabpanel.querySelector(SELECTORS.tabpanelQuote);
      const tl = gsap.timeline({ paused: true, delay: 0.1 });

      tl.set(this.mask, { width: 0, x: 0 })
        .set(tabpanelImage, { transformPerspective: 1000 })
        .to(this.mask, { duration: 0.8, width: "90%", ease: "Power4.in" })
        .to(
          this.mask,
          { duration: 0.5, x: "10%", width: "84%", ease: "Power4.in" },
          ">-0.5"
        )
        .to(
          this.mask,
          {
            duration: 0.8,
            x: "120%",
            ease: "Power4.in",
            onStart: () => this.toggleTab(tab),
          },
          ">-0.3"
        )
        .from(
          [tabpanelHeader, ...tabpanelTabs],
          {
            duration: 0.4,
            x: "-=50",
            opacity: 0,
            ease: "Power2.out",
            stagger: 0.1,
          },
          "<0.1"
        )
        .from(
          tabpanelQuote,
          { duration: 0.4, x: "+=70", autoAlpha: 0, ease: "Power2.out" },
          "<0.2"
        )
        .from(
          tabpanelImage,
          { duration: 0.3, rotateY: -45, autoAlpha: 0, ease: "Power2.out" },
          "<0.2"
        );

      this.tabpanelAnimations.push({
        dataset: tabpanel.dataset.tab,
        timeline: tl,
      });
    } else if (tabType === "search") {
      const tl = gsap.timeline({ paused: true, delay: 0.1 });

      tl.set(this.mask, { width: 0, x: 0 })
        .to(this.mask, { duration: 0.8, width: "90%", ease: "Power4.in" })
        .to(
          this.mask,
          { duration: 0.5, x: "10%", width: "84%", ease: "Power4.in" },
          ">-0.5"
        )
        .to(
          this.mask,
          {
            duration: 0.8,
            x: "120%",
            ease: "Power4.in",
            onStart: () => this.toggleTab(tab),
          },
          ">-0.3"
        )
        .from(
          tabpanel,
          { duration: 0.4, autoAlpha: 0, ease: "Power2.out" },
          "<0.1"
        );

      this.tabpanelAnimations.push({
        dataset: tabpanel.dataset.tab,
        timeline: tl,
      });
    }
  }

  animateTabpanel(tab, animate = true) {
    this.tabpanelAnimations
      .filter((tabpanel) => tabpanel.dataset === tab.dataset.tab)
      .forEach((tabPanel) => {
        if (this.reducedMotionActive || !animate) {
          tabPanel.timeline.progress(1);
        } else {
          tabPanel.timeline.restart();
        }
      });
  }

  subscribe() {
    const reducedMotionQuery = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    );

    reducedMotionQuery.addListener(() => {
      this.reducedMotionActive = reducedMotionQuery.matches;
    });
    this.reducedMotionActive = reducedMotionQuery.matches;

    this.tabs.forEach((tab) => {
      tab.addEventListener("click", (event) => {
        const currentTab = event.currentTarget;
        const isNotActiveTab = !currentTab.classList.contains(CLASSES.active);

        if (!isNotActiveTab) {
          return;
        }

        event.preventDefault();

        if (this.mask && isNotActiveTab) {
          this.animateTabpanel(currentTab, false);
        }

        this.toggleTab(currentTab);
      });
    });

    this.tabpanelDismissers.forEach((dismisser) => {
      dismisser.addEventListener("click", (event) => {
        const currentPanel = event.currentTarget;

        event.preventDefault();

        this.makeAllTabsInactive().hideTabpanel(currentPanel);
      });
    });
  }

  onToggle(fn) {
    this.onToggleCbStack = [...this.onToggleCbStack, fn];

    return this;
  }

  onClose(fn) {
    this.onCloseCbStack = [...this.onCloseCbStack, fn];

    return this;
  }

  toggleTab(tab) {
    this.makeAllTabsInactive()
      .hideAllTabpanels()
      .makeTabActive(tab)
      .showTabpanel(tab);

    this.onToggleCbStack.forEach((fn) => {
      fn(this, tab);
    });

    return this;
  }

  makeAllTabsInactive() {
    this.tabs.forEach((tab) => {
      tab.classList.remove(CLASSES.active);
      tab.setAttribute("aria-selected", "false");
      tab.blur();
    });

    return this;
  }

  hideAllTabpanels() {
    this.tabpanels.forEach((tabpanel) => {
      tabpanel.classList.add(CLASSES.hidden);
    });

    return this;
  }

  makeTabActive(element) {
    element.classList.add(CLASSES.active);
    element.setAttribute("aria-selected", "true");

    return this;
  }

  showTabpanel(element) {
    const tabUid = element.dataset.tab;

    this.getAssociatedTabpanel(tabUid).classList.remove(CLASSES.hidden);

    return this;
  }

  hideTabpanel(element) {
    const tabUid = element.dataset.tab;

    this.getAssociatedTabpanel(tabUid).classList.add(CLASSES.hidden);

    this.onCloseCbStack.forEach((fn) => {
      fn(this, element);
    });

    return this;
  }
}

export { Tabs };
